import { useQuery } from '@tanstack/react-query';
import { useRef } from 'react';
import Skeleton from 'react-loading-skeleton';
import clsx from 'clsx';

import MonitorIcon from '~/shared/assets/icons/monitor.svg';
import StorageIcon from '~/shared/assets/icons/storage.svg';
import CurrentUtilizationIcon from '~/shared/assets/icons/current-utilization.svg';
import AutoscalingIcon from '~/shared/assets/icons/auto-scale.svg';
import { VolumeCountResponse, GraphResponse, VolumesCapacityResponse } from '~/shared/types/api';
import { useXFetch } from '~/shared/utils/useXFetch';
import Typography from '~/shared/components/Typography';
import { formatGiB } from '~/shared/utils/formatNumber';
import {
  DEFAULT_REFETCH_INTERVAL_MS,
  UTILIZATION_TRAILING_LAG_MS,
} from '~/shared/constants/polling';

interface StorageSummaryCardProps {
  icon: React.ComponentType<{ className?: string }>;
  title: string;
  subText: string;
  bottomLeftContent?: React.ReactNode;
  bottomRightContent?: React.ReactNode;
}

const baseUrl = `/api/v1/volumes`;

const MONITORED_FILTER_JSON = JSON.stringify({
  field: 'monitoring',
  value:
    'monitored,managed,growing,shrinking,merging,migrating,detaching,snapshotting,modifying,unmigrating',
  op: 'in',
});

export default function KPICards() {
  const api = useXFetch();

  const { data: utilizationData, isLoading: isLoadingUtilization } = useQuery<GraphResponse>({
    queryKey: ['currentUtilizationAvg'],
    queryFn: () => {
      const nowMs = Date.now();
      const end = new Date(nowMs - UTILIZATION_TRAILING_LAG_MS);
      const start = new Date(end.getTime() - 60 * 1000);
      const qs = new URLSearchParams({
        start_date: start.toISOString(),
        end_date: end.toISOString(),
      }).toString();
      return api<GraphResponse>(`${baseUrl}/utilization-graph?${qs}`);
    },
    refetchInterval: (query) => (query.state.data ? DEFAULT_REFETCH_INTERVAL_MS : false),
  });

  const { data: capacityData, isLoading: isLoadingCapacity } = useQuery<VolumesCapacityResponse>({
    queryKey: ['volumesCapacity', {}],
    queryFn: () => api<VolumesCapacityResponse>(`${baseUrl}/capacity`),
    refetchInterval: (query) => (query.state.data ? DEFAULT_REFETCH_INTERVAL_MS : false),
  });

  const { data: totalVolumeData, isLoading: isLoadingTotalCount } = useQuery<VolumeCountResponse>({
    queryKey: ['totalVolumeCount', {}],
    queryFn: () => api<VolumeCountResponse>(`${baseUrl}/count`),
    refetchInterval: (query) => (query.state.data ? DEFAULT_REFETCH_INTERVAL_MS : false),
  });

  const monitoredApiFilterString = new URLSearchParams({
    filters: MONITORED_FILTER_JSON,
  }).toString();
  const AUTOSCALING_ON_FILTER_JSON = JSON.stringify({
    field: 'autoscalingState',
    value: 'on',
    op: 'equals',
  });
  const autoscalingOnApiFilterString = new URLSearchParams({
    filters: AUTOSCALING_ON_FILTER_JSON,
  }).toString();

  const { data: monitoredVolumeData, isLoading: isLoadingMonitoredCount } =
    useQuery<VolumeCountResponse>({
      queryKey: ['monitoredVolumeCount', MONITORED_FILTER_JSON],
      queryFn: () => api<VolumeCountResponse>(`${baseUrl}/count?${monitoredApiFilterString}`),
      refetchInterval: (query) => (query.state.data ? DEFAULT_REFETCH_INTERVAL_MS : false),
    });

  const { data: autoscalingVolumeData, isLoading: isLoadingAutoscalingCount } =
    useQuery<VolumeCountResponse>({
      queryKey: ['autoscalingVolumeCount', AUTOSCALING_ON_FILTER_JSON],
      queryFn: () => api<VolumeCountResponse>(`${baseUrl}/count?${autoscalingOnApiFilterString}`),
      refetchInterval: (query) => (query.state.data ? DEFAULT_REFETCH_INTERVAL_MS : false),
    });

  const hasTotalCapacity = typeof capacityData?.capacity === 'number';
  const totalDetectedSize = hasTotalCapacity ? `${formatGiB(capacityData.capacity)}` : undefined;
  const hasTotalCount = typeof totalVolumeData?.count === 'number';
  const totalDetectedCount = hasTotalCount ? `${totalVolumeData.count} volumes` : undefined;

  const { data: monitoredCapacityData, isLoading: isLoadingMonitoredCapacity } =
    useQuery<VolumesCapacityResponse>({
      queryKey: ['monitoredCapacity', MONITORED_FILTER_JSON],
      queryFn: () =>
        api<VolumesCapacityResponse>(`${baseUrl}/capacity?${monitoredApiFilterString}`),
      refetchInterval: (query) => (query.state.data ? DEFAULT_REFETCH_INTERVAL_MS : false),
    });

  const hasMonitoredCapacity = typeof monitoredCapacityData?.capacity === 'number';
  const monitoredStorageSize = hasMonitoredCapacity
    ? `${formatGiB(monitoredCapacityData.capacity)}`
    : undefined;
  const hasMonitoredCount = typeof monitoredVolumeData?.count === 'number';
  const monitoredStorageCount = hasMonitoredCount
    ? `${monitoredVolumeData.count} volumes`
    : undefined;

  const hasAutoscalingCount = typeof autoscalingVolumeData?.count === 'number';
  const autoscalingStorageCount = hasAutoscalingCount
    ? `${autoscalingVolumeData.count} volumes`
    : undefined;

  const { data: autoscalingCapacityData, isLoading: isLoadingAutoscalingCapacity } =
    useQuery<VolumesCapacityResponse>({
      queryKey: ['autoscalingCapacity', AUTOSCALING_ON_FILTER_JSON],
      queryFn: () =>
        api<VolumesCapacityResponse>(`${baseUrl}/capacity?${autoscalingOnApiFilterString}`),
      refetchInterval: (query) => (query.state.data ? DEFAULT_REFETCH_INTERVAL_MS : false),
    });

  const hasAutoscalingCurrent = typeof autoscalingCapacityData?.datafyCapacity === 'number';
  const hasAutoscalingOriginal = typeof autoscalingCapacityData?.capacity === 'number';
  const autoscalingCurrentSize = hasAutoscalingCurrent
    ? `${formatGiB(autoscalingCapacityData.datafyCapacity)}`
    : undefined;
  const autoscalingOriginalSize = hasAutoscalingOriginal
    ? `${formatGiB(autoscalingCapacityData.capacity)}`
    : undefined;

  // Track initial data arrival to avoid skeleton flashes on refetch
  const loadedTotalRef = useRef(false);
  if (!loadedTotalRef.current && hasTotalCapacity && hasTotalCount) {
    loadedTotalRef.current = true;
  }
  const loadedMonitoredRef = useRef(false);
  if (!loadedMonitoredRef.current && hasMonitoredCapacity && hasMonitoredCount) {
    loadedMonitoredRef.current = true;
  }
  const loadedAutoscalingRef = useRef(false);
  if (
    !loadedAutoscalingRef.current &&
    hasAutoscalingCount &&
    hasAutoscalingCurrent &&
    hasAutoscalingOriginal
  ) {
    loadedAutoscalingRef.current = true;
  }
  const loadedUtilRef = useRef(false);
  if (!loadedUtilRef.current && typeof utilizationData?.avgUtilization === 'number') {
    loadedUtilRef.current = true;
  }

  const showTotalCapacitySkeleton = !loadedTotalRef.current && isLoadingCapacity;
  const showTotalCountSkeleton = !loadedTotalRef.current && isLoadingTotalCount;
  const showMonitoredCapacitySkeleton = !loadedMonitoredRef.current && isLoadingMonitoredCapacity;
  const showMonitoredCountSkeleton = !loadedMonitoredRef.current && isLoadingMonitoredCount;
  const showAutoscalingCapacitySkeleton =
    !loadedAutoscalingRef.current && isLoadingAutoscalingCapacity;
  const showAutoscalingCountSkeleton = !loadedAutoscalingRef.current && isLoadingAutoscalingCount;
  const isLoadingCurrentUtilizationValue = !loadedUtilRef.current && isLoadingUtilization;

  const avgUtilVal = utilizationData?.avgUtilization;
  const currentUtilizationValue =
    typeof avgUtilVal === 'number' ? `${avgUtilVal.toFixed(0)}%` : 'N/A';

  return (
    <div className="w-full grid grid-cols-5 2xl:flex 3xl:grid 5xl:grid-cols-4 gap-[10px] items-start">
      {/* <div className="w-full grid grid-cols-5 5xl:grid-cols-4 gap-[10px] items-start"> */}
      <StorageSummaryCard
        icon={StorageIcon}
        title="Total storage detected"
        subText="All volumes in account"
        bottomLeftContent={
          <>
            {showTotalCapacitySkeleton ? (
              <SkeletonLoader width={80} />
            ) : (
              <Typography variant="H3-resp" fontWeight={500} color="text-primary-900" className="">
                {totalDetectedSize}
              </Typography>
            )}
          </>
        }
        bottomRightContent={
          <>
            {showTotalCountSkeleton ? (
              <SkeletonLoader width={80} />
            ) : (
              <div className={clsx('bg-gray-200 px-2 py-1 rounded')}>
                <Typography variant="p3-reg" color="text-primary-900">
                  {totalDetectedCount}
                </Typography>
              </div>
            )}
          </>
        }
      />
      <StorageSummaryCard
        icon={MonitorIcon}
        title="Monitored storage"
        subText={'Volumes monitored by Datafy'}
        bottomLeftContent={
          <>
            {showMonitoredCapacitySkeleton ? (
              <SkeletonLoader width={90} />
            ) : (
              <Typography variant="H3-resp" fontWeight={500} color="text-primary-900" className="">
                {monitoredStorageSize}
              </Typography>
            )}
          </>
        }
        bottomRightContent={
          <>
            {showMonitoredCountSkeleton ? (
              <SkeletonLoader width={80} />
            ) : (
              <div className={clsx('bg-gray-200 px-2 py-1 rounded')}>
                <Typography variant="p3-reg" color="text-primary-900">
                  {monitoredStorageCount}
                </Typography>
              </div>
            )}
          </>
        }
      />
      <div className="col-span-2 basis-2/5 5xl:col-span-1">
        <StorageSummaryCard
          icon={AutoscalingIcon}
          title="Autoscaling storage"
          subText="Volumes managed by Datafy"
          bottomLeftContent={
            <>
              {showAutoscalingCapacitySkeleton ? (
                <div className="flex items-center gap-3">
                  <SkeletonLoader width={120} />
                  <div className="h-[24px] 3xl:h-[27px] w-[1px] border border-gray-300" />
                  <SkeletonLoader width={120} />
                </div>
              ) : (
                <div className="flex itemscenter gap-1">
                  <Typography
                    variant="H3-resp"
                    fontWeight={500}
                    color="text-primary-900"
                    className="whitespace-nowrap"
                  >
                    {autoscalingCurrentSize ?? 'N/A'}
                  </Typography>
                  <p className="text-gray-500 font-[400] text-sm 2xl:text-lg whitespace-nowrap mt-auto te bg-re">
                    current
                  </p>
                  <div className="ml-2 h-[24px] 3xl:h[30px] w-[1px] border border-gray-300" />
                  <Typography
                    variant="H3-resp"
                    fontWeight={500}
                    color="text-primary-900"
                    className="whitespace-nowrap ml-2"
                  >
                    {autoscalingOriginalSize ?? 'N/A'}
                  </Typography>
                  <span className="text-gray-500 font-[400] text-sm 2xl:text-lg whitespace-nowrap mt-auto bg-gree">
                    original
                  </span>
                </div>
              )}
            </>
          }
          bottomRightContent={
            <div className="ml-5">
              {showAutoscalingCountSkeleton ? (
                <SkeletonLoader width={80} />
              ) : (
                <div className={clsx('bg-gray-200 px-2 py-1 rounded')}>
                  <Typography
                    variant="p3-reg"
                    color="text-primary-900"
                    className="whitespace-nowrap"
                  >
                    {autoscalingStorageCount}
                  </Typography>
                </div>
              )}
            </div>
          }
        />
      </div>
      <StorageSummaryCard
        icon={CurrentUtilizationIcon}
        title="Current Utilization"
        subText="Of monitored volumes"
        bottomLeftContent={
          <>
            {isLoadingCurrentUtilizationValue ? (
              <SkeletonLoader width={90} />
            ) : (
              <Typography
                variant="H3-resp"
                fontWeight={500}
                color="text-primary-900"
                className="leading-[28px]!"
              >
                {currentUtilizationValue}
              </Typography>
            )}
          </>
        }
      />
    </div>
  );
}

function StorageSummaryCard({
  icon: Icon,
  title,
  subText,
  bottomLeftContent,
  bottomRightContent,
}: StorageSummaryCardProps) {
  return (
    <div className="inline-flex p-[12px] 3xl:p-[20px] 5xl:p-[24px] flex-col justify-between gap-2 2xl:gap-3 5xl:gap-[18px] w-full  shadow-card rounded-[12px] border border-primary-100">
      <div className="flex items-center gap-2  whitespace-nowrap text-ellipsis overflow-hidden">
        <div className="size-9">{Icon && <Icon className="size-9!" />}</div>
        <div className="flex flex-col ">
          <>
            <Typography
              variant="p3"
              fontWeight={500}
              color="text-primary-900 whitespace-nowrap truncate"
              title={title}
            >
              {title}
            </Typography>
            <Typography
              variant="caption"
              color="text-gray-700 font-family-inter whitespace-nowrap truncate w-full "
              title={subText}
            >
              {subText}
            </Typography>
          </>
        </div>
      </div>
      <div className="flex gap-3 items-center justify-between font-family-outfit">
        {bottomLeftContent && <div>{bottomLeftContent}</div>}
        {bottomRightContent && <div className="ml-auto">{bottomRightContent}</div>}
      </div>
    </div>
  );
}

function SkeletonLoader({ width, className }: { width: number; className?: string }) {
  return (
    <Skeleton
      containerClassName=" block leading-[unset!important]"
      className={clsx('h-[27px] 2xl:h[30px] leading-[unset!important] rounded-[8px] ', className)}
      baseColor="var(--color-gray-200)"
      highlightColor="#FFFF"
      width={width}
    />
  );
}
